import React, { useMemo } from "react";
import Box from "@mui/material/Box";
import CardContent from "@mui/material/CardContent";
import ListSubheader from "@mui/material/ListSubheader";
import Stack from "@mui/material/Stack";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import { Link } from "react-router-dom";
import { useMyGames, useMyDoneGames } from "@seabrookstudios/pitch2table";
import { ListView } from "../core/ListView";
import { ListDetail } from "../web/ListDetail";
import { GameDetailFromRoute } from "./GameDetail";
import { GameListEntry2 } from "./ListItem";
import { CoverSquare } from "./CoverImages";
import { Path } from "../Path";
import { formatFullDate } from "../core/date-formatting";
import { useFiltersv2 } from "../core/useFilters";
import { FilterList } from "../core/FilterList";
import { GamesListFilters } from "@seabrookstudios/pitch2table-core";
import ListItem from "@mui/material/ListItem";
import List from "@mui/material/List";
import AddGameFullScreenDialog from "./AddGameFullScreenDialog";

/**
 * Games without any released edition but status Signed/Published
 */
const useGamesWithNoRelease = () => {
  const { data: games = [] } = useMyGames();
  const { data: editions = [] } = useMyDoneGames();

  const editionGameIds = useMemo(() => new Set(editions.map((edition) => edition.gameId)), [editions]);

  return useMemo(() => {
    return games.filter((game) => {
      const matchesStatus = ["Signed", "Published"].includes(game.status);
      const hasEdition = editionGameIds.has(game.id);
      return matchesStatus && !hasEdition;
    });
  }, [games, editionGameIds]);
};

const EditionListItem = ({ edition, game }) => {
  const to = Path.game(edition.gameId).link();
  const description = edition.datePublished
    ? `${formatFullDate(edition.datePublished)} • ${edition.name}`
    : edition.name;
  const title = game?.name || edition.name;
  const coverGame = game && edition.image ? { ...game, image: edition.image } : undefined;

  return (
    <ListItemButton id={`edition-${edition.id}`} alignItems="flex-start" to={to} component={Link}>
      <ListItemAvatar>
        {coverGame && <CoverSquare game={coverGame} width={48} height={48} />}
      </ListItemAvatar>
      <ListItemText primary={title} secondary={description} />
    </ListItemButton>
  );
};

const CustomWebFilters = GamesListFilters.filter((filter) => filter.label !== "DUE");

const DoneList = () => {
  const editionsQuery = useMyDoneGames();
  const {
    data: editions = [],
    isRefetching: editionsRefetching,
    refetch: refetchEditions,
    isLoading: editionsLoading,
    isError: editionsError,
  } = editionsQuery;
  const gamesQuery = useMyGames();
  const {
    data: games = [],
    isLoading: gamesLoading,
    isRefetching: gamesRefetching,
    refetch: refetchGames,
    isError: gamesError,
  } = gamesQuery;
  const gamesWithNoRelease = useGamesWithNoRelease();

  const gameMap = useMemo(() => new Map(games.map((g) => [g.id, g])), [games]);

  const filterConfiguration = useFiltersv2(gamesQuery, CustomWebFilters, "games");

  const sections = useMemo(() => {
    const editionsWithNoRelease = editions.filter((edition) => !edition.datePublished);
    const byYear = editions.reduce((map, edition) => {
      if (!edition.datePublished) {
        return map;
      }
      const year = new Date(edition.datePublished).getUTCFullYear();
      const existing = map.get(year);
      if (existing) {
        existing.push(edition);
      } else {
        map.set(year, [edition]);
      }
      return map;
    }, new Map());

    const years = [...byYear.keys()].sort((a, b) => b - a);
    const orderedSections = years.map((year) => {
      const data = [...(byYear.get(year) || [])].sort((a, b) => (b.datePublished || 0) - (a.datePublished || 0));
      return { title: `${year}`, data };
    });

    const noReleaseItems = [...editionsWithNoRelease, ...gamesWithNoRelease];
    const noReleaseSection = noReleaseItems.length ? [{ title: "No Release Date", data: noReleaseItems }] : [];

    return /** @type {Array<{ title: string; data: any[] }>} */ ([...noReleaseSection, ...orderedSections]);
  }, [editions, gamesWithNoRelease]);

  const totalGames = useMemo(() => {
    const gameIds = new Set();
    const expansionIds = new Set();

    editions.forEach((e) => {
      const game = gameMap.get(e.gameId);
      if (!game) {
        return; // Skip if game not found
      }

      // Treat undefined/null gameType as "game" (default value)
      if (game.gameType === "expansion") {
        expansionIds.add(e.gameId);
      } else {
        gameIds.add(e.gameId);
      }
    });

    gamesWithNoRelease.forEach((g) => {
      // Treat undefined/null gameType as "game" (default value)
      if (g.gameType === "expansion") {
        expansionIds.add(g.id);
      } else {
        gameIds.add(g.id);
      }
    });

    return { games: gameIds.size, expansions: expansionIds.size };
  }, [editions, gamesWithNoRelease, gameMap]);

  const totalEditions = editions.length;

  const refetch = () => {
    refetchEditions();
    refetchGames();
  };

  const isLoading = editionsLoading || gamesLoading;
  const isRefetching = editionsRefetching || gamesRefetching;
  const isError = editionsError || gamesError;

  return (
    <ListView
      title="My Games"
      shown={sections.reduce((acc, s) => acc + s.data.length, 0)}
      refetch={refetch}
      isLoading={isLoading}
      isRefetching={isRefetching}
      isError={!!isError}
      Filters={() => (
        <FilterList
          filters={/** @type {any} */ (filterConfiguration.visibleFilters)}
          filter={filterConfiguration.filter}
          onFilterClicked={filterConfiguration.onFilterClicked}
        />
      )}
      Action={AddGameFullScreenDialog}
    >
      <CardContent>
        <Box p={1}>
          <Stack direction="row" justifyContent="space-between">
            <Box>
              <ListItem>
                <ListItemText primary="Games" secondary={totalGames.games} />
              </ListItem>
            </Box>
            <Box>
              <ListItem>
                <ListItemText style={{ textAlign: "center" }} primary="Expansions" secondary={totalGames.expansions} />
              </ListItem>
            </Box>
            <Box>
              <ListItem>
                <ListItemText style={{ textAlign: "right" }} primary="Editions" secondary={totalEditions} />
              </ListItem>
            </Box>
          </Stack>
        </Box>

        {sections.map((section) => (
          <React.Fragment key={section.title}>
            <ListSubheader>{section.title}</ListSubheader>
            <List>
              {section.data.map((item) => {
                const isEdition = item && "gameId" in item && "datePublished" in item;
                if (isEdition) {
                  const game = gameMap.get(item.gameId);
                  return <EditionListItem key={`edition-${item.id}`} edition={item} game={game} />;
                }
                return <GameListEntry2 key={`game-${item.id}`} item={item} noAlert />;
              })}
            </List>
          </React.Fragment>
        ))}
      </CardContent>
    </ListView>
  );
};

export const DoneGamesListAndDetail = () => {
  return <ListDetail ListView={DoneList} DetailView={GameDetailFromRoute} />;
};
